<script setup lang="ts">
import { ref } from 'vue';
import { computed } from 'vue';
import { useEcomStore } from '@/stores/apps/eCommerce';
import { useStoresStore } from '@/stores/apps/store';
import { useThirdpartyStore } from '@/stores/apps/thirdparty';
import { formatCurrency } from '@/utils/format';
import OrderSummary from '@/components/apps/ecommerce/cart/OrderSummary.vue';
import CartEmpty from '@/components/apps/ecommerce/cart/CartEmpty.vue';
import CartCheckout from '@/components/apps/ecommerce/cart/CartCheckout.vue';
import { IconBuildingStore, IconPlus, IconMinus, IconTrash, IconCheck, IconQrcode, IconShoppingCart, IconRefresh } from '@tabler/icons-vue';
import { useI18n } from 'vue-i18n'
import { formatNumber } from '@/utils/format';
const thirdpartyStore = useThirdpartyStore();
const ecomStore = useEcomStore();

const { t } = useI18n()
const isThirdpartySelected = computed(() => thirdpartyStore.isThirdpartySelected ? true : false);

import _ from 'lodash';
import { is } from 'date-fns/locale';
const successSnackbar = ref<boolean>(false);
const itemAction = ref<string>('');

const getCart = computed(() => {
    return ecomStore.getCart;
});

const route = useRoute();

const validateMaxItems = computed(() => {
    return false;
    // const maxItems = stores.maximumItems;
    // const actualItems = ecomStore.getTotalQty || 0;
    // return actualItems >= maxItems;
});

const getTotalQty = computed(() => {
    return ecomStore.getTotalQty;
});

defineProps<{
    isOrderSummary: boolean
}>()


onMounted(async () => {
    await ecomStore.validateProducts();
});

const handleItemAction = (action: string) => {
    itemAction.value = action;
    successSnackbar.value = true;
}
</script>
<template>
    <div v-if="getCart.length > 0">
        <div v-if="isOrderSummary">
            <h5 class="text-h5">Cliente:</h5> 
            <span class="d-flex align-center" :class="isThirdpartySelected ? 'text-info' : 'text-error'">
                <IconBuildingStore stroke-width="1.5" size="22" class="mr-1" /> {{ isThirdpartySelected ? thirdpartyStore.thirdparty?.social_reason ?? 'Seleccionar Cliente' : 'Seleccionar Cliente' }}
            </span>
        </div>
        <h5 class="text-h5 mt-5">{{ $t('Cart Item')}} ({{ getTotalQty }})</h5>
        <v-card :elevation="isOrderSummary ? 0 : 1">
            <v-card-item class="py-0" :class="isOrderSummary ? 'px-0' : 'px-2'">
                <v-container>
                    <div v-if="!isOrderSummary" class="v-md-row font-weight-bold d-none d-md-flex">
                        <v-col cols="12" md="5" class="text-left text-subtitle-1 font-weight-semibold">{{ $t('Product') }}</v-col>
                        <v-col cols="2" class="text-center text-subtitle-1 font-weight-semibold" >{{ $t('Price') }}</v-col>
                        <v-col cols="2" class="text-center text-subtitle-1 font-weight-semibold" >{{ $t('Quantity') }}</v-col>
                        <v-col cols="2" class="text-center text-subtitle-1 font-weight-semibold" >Total</v-col>
                        <v-col class="text-center"></v-col>
                    </div>
                    <v-row v-for="item in getCart" :key="item.name" style="border-bottom: thin solid rgba(var(--v-border-color), 1) !important;" :class="isOrderSummary ? 'py-2' : 'py-2'">
                        <v-col :cols="isOrderSummary ? 12 : 5" class="px-0 px-md-auto py-0">
                            <div class="d-flex align-center gap-md-2">
                                <img alt="product" class="rounded-md" :class="isOrderSummary ? 'custom-img-box-mini' : 'custom-img-box'" 
                                    style="background: radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(164,167,176,0.2) 100%)" 
                                    :src="item.image" />
                                <div class="ma-0 ml-2 ma-md-2">
                                    <div class="text-subtitle-1 font-weight-light">
                                        {{ item.description }}
                                        <span class="d-none d-md-block text-xs text-grey-lighten">{{ item.ean ?? item.sku ?? item.id }}</span>
                                    </div>
                                    <div class="d-none d-md-block">
                                        <v-chip v-if="item.discount > 0" color="success" variant="flat" size="x-small" class="mr-1 pa-1" rounded="md">
                                            <IconCheck size="14" /> {{ item.discount.toFixed(0) }}%
                                        </v-chip>
                                        <v-chip v-if="item.discountCard > item.discount" color="info" variant="flat" size="x-small" class="mr-1 pa-1" rounded="md">
                                            +{{ item.discountCard.toFixed(0) - item.discount.toFixed(0) }}%
                                        </v-chip>
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex d-md-none justify-space-between align-center my-0">
                                <v-sheet>
                                    <v-chip v-if="item.discount > 0" color="success" variant="flat" size="x-small" class="mr-1 pa-1 pa-md-2" rounded="md">
                                        <IconCheck size="14" /> {{ item.discount.toFixed(0) }}%
                                    </v-chip>
                                    <v-chip v-if="item.discountCard > item.discount" color="info" variant="flat" size="x-small" class="mr-1 pa-1 pa-md-2" rounded="md">
                                        +{{ item.discountCard.toFixed(0) - item.discount.toFixed(0) }}%
                                    </v-chip>
                                </v-sheet>
                                <v-sheet v-if="!isOrderSummary">
                                    <v-btn-toggle color="transparent" density="compact">
                                        <v-btn v-if="item.qty >= 2 && item.unit !== 'KG'" size="x-small" variant="plain" @click="ecomStore.decrementQty(item.pk)" >
                                            <IconMinus size="18" />
                                        </v-btn>
                                        <v-btn v-if="item.qty === 1 || item.unit === 'KG'" size="x-small" variant="plain">
                                            <IconTrash size="18" class="text-error" @click="ecomStore.deleteProduct(item.pk)" />
                                        </v-btn>
                                        <v-btn size="x-small" class="text-subtitle-2 text-right" variant="plain">
                                            {{ item.qty }}{{ item.unit === 'KG' ? ' KG' : '' }}
                                        </v-btn>
                                        <v-btn v-if="item.unit !== 'KG'" size="x-small" :disabled="validateMaxItems" @click="ecomStore.incrementQty(item.pk)" variant="plain">
                                            <IconPlus size="18" />
                                        </v-btn>
                                    </v-btn-toggle>
                                </v-sheet>
                            </div>
                            <div v-if="isOrderSummary" class="d-flex align-center justify-space-between text-right">
                                <div v-if="item.offerPrice">
                                    <h4 class="text-h5">{{ formatCurrency(item.offerPrice) }}</h4>
                                    <p class="text-decoration-line-through text-medium-emphasis">{{ formatCurrency(item.price) }}</p>
                                </div>
                                <div v-else>
                                    <span class="font-weight-bold">{{ formatCurrency(item.price) }}</span>
                                </div>
                                <div>
                                    <span class="text-subtitle-1">{{ formatNumber(item.qty) }} unds</span>
                                </div>
                                <div>
                                    <span class="font-weight-bold">{{ formatCurrency(item.offerPrice ? item.offerPrice * item.qty : item.price * item.qty) }}</span>
                                </div>
                            </div>
                        </v-col>
                        <v-col v-if="!isOrderSummary" cols="2" class="d-none d-md-flex flex-md-column align-center justify-center py-0">
                            <div v-if="item.offerPrice">
                                <h4 class="text-h5">{{ formatCurrency(item.offerPrice) }}</h4>
                                <p class="text-decoration-line-through text-medium-emphasis">{{ formatCurrency(item.price) }}</p>
                            </div>
                            <div v-else>
                                <h4 class="text-h5">{{ formatCurrency(item.price) }}</h4>
                            </div>
                        </v-col>
                        <v-col v-if="!isOrderSummary" cols="2" class="text-center d-none d-md-flex flex-md-column align-center justify-center py-0">
                            <div >
                                <div v-if="item.isStock" class="d-flex align-center justify-center"> 
                                    <v-text-field
                                        v-model="item.qty"
                                        type="number"
                                        class="mr-2"
                                        min="1"
                                        :max="item.stock"
                                        density="compact"
                                        hide-details
                                        style="max-width: 96px"
                                    ></v-text-field>
                                    <v-btn 
                                        v-if="getCart.find((x:any)=>x.pk === item.pk).qty !== item.qty"
                                        @click="async () => { await ecomStore.updateQty(item.pk, item.qty); handleItemAction('updated') }" 
                                        :disabled ="parseFloat(item.qty) < 1 || parseFloat(item.qty) > (item.stock ?? 0)"
                                        color="primary"
                                        variant="flat" icon density="compact">
                                        <IconRefresh size="16" />
                                    </v-btn>
                                </div>
                                <span v-if="item.stock > parseFloat(item.qty)" class="text-xs">{{ formatNumber(item.qty) }} de {{ formatNumber(item.stock) }}</span>

                                <span v-else-if="item.stock < Math.round(item.qty)" class="text-xs text-error">Máximo {{ formatNumber(item.stock) }}</span>
                                <span v-else class="text-xs text-error">{{ formatNumber(item.qty) }} de {{ formatNumber(item.stock) }}</span>
                            </div>
                        </v-col>
                        <v-col v-if="!isOrderSummary" cols="2" class="pr-8 d-none d-md-flex flex-md-column align-center justify-center py-0" >
                            <h5 class="text-h5">{{ formatCurrency(item.offerPrice ? item.offerPrice * item.qty : item.price * item.qty) }}</h5>
                            <p v-if="item.offerPrice" class="text-success-darken-2 text-caption ">{{ formatCurrency(item.offerPrice ? (item.price-item.offerPrice) * item.qty * -1 : 0) }}</p>
                            <p v-if="item.offerCardPrice" class="text-info text-caption ">{{ formatCurrency(item.offerCardPrice ? (item.price-item.offerCardPrice) * item.qty * -1 : 0)}}</p>
                        </v-col>
                        <v-col v-if="!isOrderSummary" cols="1" class="d-none d-md-flex flex-md-column align-center justify-center py-0">
                            <v-btn @click="() => { ecomStore.deleteProduct(item.pk); tempCart = _.cloneDeep(ecomStore.getCart) }" small icon flat elevation="0">
                                <IconTrash size="18" class="text-error" />
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-item>
            <v-snackbar v-model="successSnackbar" variant="flat" location="bottom right" color="success" rounded="md" class="text-white" >
                Producto {{ itemAction === 'updated' ? 'Actualizado' : itemAction === 'removed' ? 'Eliminado' : 'Gestionado' }}
            </v-snackbar>
        </v-card>
    </div>
    <div v-else-if="!isOrderSummary" class="d-flex justify-center">
        <CartEmpty />
    </div>
</template>
<style scoped lang="scss">
.custom-img-box {
    width: 60px;
    height: 60px;
}
.custom-img-box-mini {
    width: 44px;
    height: 44px;
}
</style>
